// Minimal cloud helper layer for Explorer-hosted SRA media controls.

// ---- Session-death notification --------------------------------------
// The apps authenticate two ways at once: survey calls carry the X-Org-ID /
// X-User-ID headers cached at page load, while cookie-authenticated routes
// (media-refs, package-refs, /me) need a live Hanko session. A session that
// expires in an open tab therefore leaves the first kind working and the
// second kind 401ing, which surfaced as inexplicable save failures instead of
// "you are signed out". Any 401 now tells AuthContext, which drops the user
// and lets the gate render the sign-in form.
let __onUnauthorized = null;

/** Registered once by AuthContext. */
export function setUnauthorizedHandler(fn) {
  __onUnauthorized = typeof fn === 'function' ? fn : null;
}

/** Call with any dbam response; reports a 401 exactly once per response. */
export function noteAuthFailure(resp) {
  if (resp && resp.status === 401 && __onUnauthorized) {
    try { __onUnauthorized(); } catch (_) { /* never let this break a caller */ }
  }
}

// Uses the Explorer Express proxy so browser code does not call sam-dbam directly.

const API_BASE = '/api';

async function httpRequest(url, options = {}, { timeoutMs = 10000, responseType = 'json' } = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const resp = await fetch(url, { ...options, signal: controller.signal });
        if (!resp.ok) {
          noteAuthFailure(resp);
            let errText = '';
            try { errText = await resp.text(); } catch (_) { }
            const error = new Error(errText || resp.statusText || 'Request failed');
            error.status = resp.status;
            throw error;
        }
        if (responseType === 'json') return await resp.json();
        return await resp.text();
    } catch (err) {
        if (err && (err.name === 'AbortError' || /aborted/i.test(String(err.message)))) {
            const timeoutError = new Error(`Request timed out after ${timeoutMs} ms`);
            timeoutError.code = 'ETIMEOUT';
            throw timeoutError;
        }
        throw err;
    } finally {
        clearTimeout(timer);
    }
}

function unwrap(resp) {
    if (!resp || typeof resp !== 'object') return resp;
    if (resp.data !== undefined) {
        const inner = resp.data;
        if (inner && typeof inner === 'object' && Array.isArray(inner.data)) return inner.data;
        return inner;
    }
    return resp;
}

export async function getAudioSignedUrl(gcsPath) {
    const resp = await httpRequest(
        `${API_BASE}/audio/signed-url?gcsPath=${encodeURIComponent(gcsPath)}`,
        { method: 'GET' },
        { timeoutMs: 8000 }
    );
    const data = unwrap(resp) || {};
    const url = data.url || resp.url;
    if (!url) throw new Error('No signed URL returned');
    return url;
}

export async function deleteAudioRecording(gcsPath) {
    return httpRequest(
        `${API_BASE}/audio`,
        {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ gcsPath }),
        },
        { timeoutMs: 8000 }
    );
}

export async function getFileUploads(sessionId) {
    const resp = await httpRequest(
        `${API_BASE}/file-uploads?session_id=${encodeURIComponent(sessionId)}`,
        { method: 'GET' },
        { timeoutMs: 8000 }
    );
    const data = unwrap(resp);
    if (Array.isArray(data)) return data;
    return data?.files || [];
}

export async function getFileSignedUrl(gcsPath, filename) {
    let url = `${API_BASE}/file-uploads/signed-url?gcs_path=${encodeURIComponent(gcsPath)}`;
    if (filename) url += `&filename=${encodeURIComponent(filename)}`;
    const resp = await httpRequest(url, { method: 'GET' }, { timeoutMs: 8000 });
    const data = unwrap(resp) || {};
    const signedUrl = data.url || resp.url;
    if (!signedUrl) throw new Error('No signed URL returned');
    return signedUrl;
}

export async function getBulkFileUrls(gcsPaths) {
    if (!Array.isArray(gcsPaths) || gcsPaths.length === 0) return [];
    const requests = gcsPaths.map(async (gcsPath) => {
        const url = await getFileSignedUrl(gcsPath);
        return { gcs_path: gcsPath, url, filename: decodeURIComponent(String(gcsPath).split('/').pop() || 'file') };
    });
    return Promise.all(requests);
}
